import { type FC, Fragment, type MouseEvent, useMemo, useState } from "react";
import {
  HeaderCellTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from "../../../../common/components/Table/TableElements";
import ShipmentPackageInformation from "./ShipmentPackageInformation";
import PackageTableProductRow from "./PackageTableProductRow";
import { type WeightData } from "../../../../models/WeightData";
import { SortDirection, useSorting } from "../../../../common/hooks/useSorting";
import Chip from "../../../../common/components/Chip/Chip";
import ItemBlock from "../../../../common/components/print/ItemBlock";
import { NewVersionMessage } from "../../../../common/components/Messages/NewVersionMessage";
import classNames from "classnames";
import {
  type DeliveryEntryData,
  type InvoiceInfoData,
  type ShipmentInformationDataWrapper,
} from "../../../common/models";
import { type OrderLineItemData } from "../../../common/modelsV2";
import { useTranslation } from "react-i18next";
import { hasSuccessor } from "~/utils/productUtils";

enum SortKinds {
  Shipping,
  ProductName,
  Quantity,
  PackagingCode,
  Date,
  Price,
  Total,
}

const SORT_HANDLERS: Dict<(a: ShippingItem, b: ShippingItem) => number> = {
  [SortKinds.Shipping]: (a, b) => a.packageId.localeCompare(b.packageId),
  [SortKinds.ProductName]: (a, b) => a.entry.product.designation.localeCompare(b.entry.product.designation),
  [SortKinds.Quantity]: (a, b) => a.entry.qty - b.entry.qty,
  [SortKinds.PackagingCode]: (a, b) => {
    if (a.entry.packCode === undefined && b.entry.packCode === undefined) {
      return 0;
    }
    if (a.entry.packCode === undefined) {
      return 1;
    }
    if (b.entry.packCode === undefined) {
      return -1;
    }
    return parseInt(a.entry.packCode) - parseInt(b.entry.packCode);
  },
  [SortKinds.Date]: (a, b) => {
    const aTime = a.entry.deliveryDate?.getTime() || 0;
    const bTime = b.entry.deliveryDate?.getTime() || 0;
    return aTime === bTime ? 0 : aTime > bTime ? 1 : -1;
  },
  [SortKinds.Price]: (a, b) => a.entry.netPrice.value - b.entry.netPrice.value,
  [SortKinds.Total]: (a, b) => a.entry.netPrice.value * a.entry.qty - b.entry.netPrice.value * b.entry.qty,
};

export interface ShippingItem {
  packageNumber: number;
  totalPackages: number;
  packageId: string;
  invoiceData?: InvoiceInfoData;
  weightData: WeightData;
  entriesNumber: number;
  entry: OrderLineItemData;
  showShippingHeader?: boolean;
  actualGIDate: Date | undefined;
  plannedGIDate: Date | undefined;
  validHandlingUnit: boolean;
}

interface PackageTableProps {
  deliveryEntryData: DeliveryEntryData;
  shipmentInformationDataWrapper: ShipmentInformationDataWrapper;
  "data-test-id"?: string;
}

const PackageTable: FC<PackageTableProps> = ({ deliveryEntryData, shipmentInformationDataWrapper }) => {
  const { t } = useTranslation();

  const [sortCode, setSortCode] = useState(SortKinds.Shipping);
  const [sortDirection, setSortDirection] = useState(SortDirection.Asc);

  const packageRows: ShippingItem[] = useMemo(() => {
    const packageRowsList: ShippingItem[] = [];
    Object.keys(deliveryEntryData.packages).forEach((key, index) => {
      Object.values(shipmentInformationDataWrapper.orderDetailsData[key]).forEach((orderEntry, innerIndex) => {
        const rowData: ShippingItem = {
          packageNumber: index,
          totalPackages: Object.keys(deliveryEntryData.packages).length,
          packageId: key,
          invoiceData: shipmentInformationDataWrapper.orderDetailsData[key][0].invoiceData,
          weightData: deliveryEntryData.packages[key][0].totalWeight,
          entriesNumber: Object.values(shipmentInformationDataWrapper.orderDetailsData[key]).length,
          entry: orderEntry,
          actualGIDate: deliveryEntryData.actualGIDate,
          plannedGIDate: deliveryEntryData.plannedGIDate,
          validHandlingUnit:
            deliveryEntryData.packages[key][index] === undefined
              ? true
              : deliveryEntryData.packages[key][index].validHandlingUnit,
        };
        const lastEntry = packageRowsList[packageRowsList.length - 1] || undefined;
        rowData.showShippingHeader = lastEntry === undefined || key !== lastEntry.packageId ? true : false;
        packageRowsList.push(rowData);
      });
    });

    return packageRowsList;
  }, [deliveryEntryData.packages, shipmentInformationDataWrapper.orderDetailsData]);

  const rows = useSorting(packageRows, SORT_HANDLERS, sortCode, sortDirection);

  let rowIndex = 0;
  useMemo(() => {
    const headersEntries: ShippingItem[] = [];
    rows.forEach((row: ShippingItem, index: number) => {
      ++rowIndex;
      const lastHeader: ShippingItem = headersEntries[headersEntries.length - 1] || undefined;

      if (index === 0 || (index > 0 && row.packageId !== rows[index - 1].packageId)) {
        row.showShippingHeader = true;
        if (lastHeader) {
          lastHeader.entriesNumber = rowIndex;
        }
        rowIndex = 0;
      } else {
        row.showShippingHeader = false;
      }

      if (row.showShippingHeader) {
        headersEntries.push(row);
      }
    });

    // set the nr entries for the last settet header
    if (headersEntries.length > 0) {
      headersEntries[headersEntries.length - 1].entriesNumber = rowIndex + 1;
    }
  }, [rows]);

  const createSortHandler = (codeToSet: SortKinds) => (e: MouseEvent<unknown>) => {
    if (codeToSet === sortCode) {
      setSortDirection(sortDirection === SortDirection.Asc ? SortDirection.Desc : SortDirection.Asc);
    } else {
      setSortCode(codeToSet);
      setSortDirection(SortDirection.Asc);
    }
  };

  const createTableProps = (boundSortCode: SortKinds) => ({
    onClick: createSortHandler(boundSortCode),
    active: boundSortCode === sortCode,
    direction: boundSortCode === sortCode ? sortDirection : SortDirection.Desc,
  });

  const testIdSorting = `sorting-${sortDirection ? "descending" : "ascending"}`; // ${isSelected ? "sorting-selected" : ""} TODO: Add later
  return (
    <div className={`mt-30`}>
      <Table className={"scha__order-details-page-shipment-row"}>
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel {...createTableProps(SortKinds.Shipping)} data-test-id={"package-information"}>
                <HeaderCellTitle
                  //data-test-id={testIdSorting}
                  header={t("quoteDetailsPage.table.header.packageInfo")}
                />
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel {...createTableProps(SortKinds.ProductName)} data-test-id={"product"}>
                <HeaderCellTitle
                  //data-test-id={testIdSorting}
                  header={t("quoteDetailsPage.table.header.product")}
                />
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel {...createTableProps(SortKinds.Quantity)} data-test-id={"quantity"}>
                <HeaderCellTitle
                  //data-test-id={testIdSorting}
                  header={t("quoteDetailsPage.table.header.quantity")}
                  subheader={t("quoteDetailsPage.summary.shippedQuantity")}
                />
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel {...createTableProps(SortKinds.PackagingCode)} data-test-id={"packaging-code"}>
                <HeaderCellTitle
                  //data-test-id={testIdSorting}
                  header={t("quoteDetailsPage.summary.packagingCode")}
                  subheader={t("cartPage.table.header.packaging.subtitle")}
                />
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel {...createTableProps(SortKinds.Date)} data-test-id={"delivery-date"}>
                <HeaderCellTitle
                  //data-test-id={testIdSorting}
                  header={t("activeQuotePage.table.header.delivery.title")}
                  subheader={t("order.shipment.package.goodsIssueDate")}
                />
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel {...createTableProps(SortKinds.Price)} data-test-id={"net-price"}>
                <HeaderCellTitle
                  //data-test-id={testIdSorting}
                  header={t("cartPage.table.header.price.title")}
                  subheader={t("quoteDetailsPage.table.header.priceUnit")}
                />
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel {...createTableProps(SortKinds.Total)} data-test-id={"total-price"}>
                <HeaderCellTitle
                  //data-test-id={testIdSorting}
                  header={t("orderDetailsPage.table.header.total")}
                />
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows &&
            rows.map((item: ShippingItem, index, rows) => (
              <Fragment key={index}>
                <TableRow
                  className={classNames(
                    "scha__order-details-page-shipment-row",
                    !hasSuccessor(item.entry.product) && "scha__order-details-page-shipment-row--last"
                  )}
                >
                  {item.showShippingHeader && (
                    <TableCell
                      className={"scha__order-details-page-shipment-row__cell__package-info"}
                      rowSpan={calculateRowSpan(index, rows)}
                    >
                      <ItemBlock
                        className={"scha__print-only"}
                        mainTitle={t("quoteDetailsPage.table.header.packageInfo")}
                        mainValue={" "}
                      />
                      {item.packageId ? (
                        <ShipmentPackageInformation
                          packageIndex={item.packageNumber}
                          totalPackageCount={item.totalPackages}
                          packageId={item.packageId}
                          invoiceInfoData={item.invoiceData}
                          weightData={item.weightData}
                          validHandlingUnit={item.validHandlingUnit}
                        />
                      ) : (
                        <Chip type="grey">{t("quoteDetailsPage.table.pending")}</Chip>
                      )}
                    </TableCell>
                  )}
                  <PackageTableProductRow item={item} />
                </TableRow>

                {hasSuccessor(item.entry.product) && (
                  <TableRow
                    className={"scha__order-details-page-shipment-row scha__order-details-page-shipment-row--last"}
                  >
                    <TableCell colSpan={3}>
                      <NewVersionMessage baseProduct={item.entry.product} />
                    </TableCell>
                    <TableCell colSpan={3} />
                  </TableRow>
                )}
              </Fragment>
            ))}
        </TableBody>
      </Table>
    </div>
  );
};

const calculateRowSpan = (startingIndex: number, rows: ShippingItem[]) => {
  let total = rows[startingIndex].entriesNumber;
  let i = startingIndex;
  let row = rows[i];

  do {
    if (hasSuccessor(row.entry.product)) {
      total += 1;
    }
    row = rows[++i];
  } while (row && !row.showShippingHeader);

  return total;
};

export default PackageTable;
